import { useEffect, useState } from "react";
import { Sparkles, Loader2, Bookmark, BookmarkPlus, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";

// Genera con AI il contenuto di un modello lettera (con variabili) e lo inserisce nell'editor.
export default function TemplateAIButton({ editorRef, titolo, testId = "tpl-ai" }) {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [saveName, setSaveName] = useState(null);
  const [editId, setEditId] = useState(null);
  const loadSaved = () => api.get("/studio/lettera-ai/prompts", { params: { scope: "template" } }).then(({ data }) => setSaved(data || [])).catch(() => setSaved([]));
  useEffect(() => {
    api.get("/studio/lettera-ai/enabled").then(({ data }) => setEnabled(!!data?.enabled)).catch(() => setEnabled(false));
  }, []);
  useEffect(() => { if (open && enabled) loadSaved(); }, [open, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!enabled) return null;
  const salva = async () => {
    const titolo = (saveName || "").trim();
    if (!titolo || !prompt.trim()) { toast.error("Inserisci un nome e una descrizione"); return; }
    try {
      if (editId) await api.put(`/studio/lettera-ai/prompts/${editId}`, { titolo, testo: prompt.trim(), scope: "template" });
      else await api.post("/studio/lettera-ai/prompts", { titolo, testo: prompt.trim(), scope: "template" });
      toast.success(editId ? "Descrizione aggiornata" : "Descrizione salvata");
      setSaveName(null); setEditId(null); loadSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore nel salvataggio"); }
  };
  const elimina = async (s) => {
    if (!window.confirm(`Eliminare la descrizione «${s.titolo}»?`)) return;
    try { await api.delete(`/studio/lettera-ai/prompts/${s.id}`); loadSaved(); } catch { toast.error("Errore nell'eliminazione"); }
  };
  const genera = async () => {
    if (!prompt.trim()) { toast.error("Descrivi il tipo di lettera da generare"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/studio/letter-templates/ai-genera", { prompt: prompt.trim(), titolo: titolo || "" });
      const ed = editorRef?.current;
      if (!ed) { toast.error("Editor non pronto"); return; }
      if (ed.isEmpty) ed.commands.setContent(data.html, true);
      else ed.chain().focus().insertContent(data.html).run();
      const unk = data.variabili_sconosciute || [];
      toast.success("Testo generato e inserito nell'editor");
      if (unk.length) toast.warning(`Variabili non riconosciute da controllare: ${unk.join(", ")}`);
      setOpen(false); setPrompt("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore nella generazione");
    } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`${testId}-btn`}
        className="inline-flex items-center gap-1 rounded-md border border-violet-500/50 px-2 py-1 text-xs font-semibold text-violet-300 transition-colors hover:bg-violet-500/10">
        <Sparkles className="h-3.5 w-3.5" />Genera con AI
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-lg bg-[#0f1420] text-slate-100 border border-slate-800" data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle>Genera modello con AI</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">Descrivi la lettera: l'AI scrive il testo completo con le variabili della pratica già inserite (es. {"{{cliente.cognome:title}}"}, {"{{pratica.numero:upper}}"}). Ogni generazione è indipendente dalle precedenti.</p>
          {saved.length > 0 && (
            <div className="space-y-1" data-testid={`${testId}-saved`}>
              <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><Bookmark className="h-3 w-3" />Descrizioni salvate</div>
              <div className="flex flex-wrap gap-1.5">
                {saved.map((s) => (
                  <span key={s.id} className="inline-flex items-center rounded-full border border-violet-500/50 bg-violet-500/10 text-xs font-medium text-violet-500">
                    <button type="button" onClick={() => setPrompt(s.testo)} title={s.testo} data-testid={`${testId}-saved-use-${s.id}`} className="px-2.5 py-1 hover:text-violet-700 transition-colors">{s.titolo}</button>
                    <button type="button" onClick={() => { setPrompt(s.testo); setSaveName(s.titolo); setEditId(s.id); }} title="Modifica" data-testid={`${testId}-saved-edit-${s.id}`} className="px-1 text-violet-400 hover:text-violet-700"><Pencil className="h-3 w-3" /></button>
                    <button type="button" onClick={() => elimina(s)} title="Elimina" data-testid={`${testId}-saved-del-${s.id}`} className="pl-1 pr-2 text-violet-400 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5} autoFocus data-testid={`${testId}-prompt`}
            placeholder="Es. Richiesta di risarcimento danni alla compagnia della controparte con messa in mora e allegati"
            className="w-full resize-y rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-violet-500/60" />
          {saveName === null ? (
            <button type="button" onClick={() => { setSaveName(""); setEditId(null); }} disabled={!prompt.trim()} data-testid={`${testId}-save-open`}
              className="inline-flex items-center gap-1 self-start text-xs font-semibold text-violet-300 hover:text-violet-200 disabled:opacity-40"><BookmarkPlus className="h-3.5 w-3.5" />Salva questa descrizione</button>
          ) : (
            <div className="flex items-center gap-1.5">
              <input value={saveName} onChange={(e) => setSaveName(e.target.value)} autoFocus placeholder="Nome della descrizione (es. Richiesta danni)" data-testid={`${testId}-save-name`}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); salva(); } }}
                className="h-8 flex-1 rounded-md border border-slate-700 bg-slate-900/60 px-2 text-xs text-slate-100" />
              <button type="button" onClick={salva} data-testid={`${testId}-save-confirm`} title={editId ? "Aggiorna" : "Salva"} className="h-8 w-8 flex items-center justify-center rounded-md border border-emerald-600/60 text-emerald-400 hover:bg-emerald-500/10"><Check className="h-4 w-4" /></button>
              <button type="button" onClick={() => { setSaveName(null); setEditId(null); }} data-testid={`${testId}-save-cancel`} title="Annulla" className="h-8 w-8 flex items-center justify-center rounded-md border border-slate-600 text-slate-400 hover:bg-slate-700/40"><X className="h-4 w-4" /></button>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} data-testid={`${testId}-cancel`}>Annulla</Button>
            <Button onClick={genera} disabled={busy} data-testid={`${testId}-generate`} className="bg-violet-600 hover:bg-violet-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}{busy ? "Generazione…" : "Genera e inserisci"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
